/**
 * Dealer API client — H-1 hardening migration (Option B).
 *
 * Wraps axios so every dealer request automatically carries the JWT
 * in the ``Authorization: Bearer`` header instead of the legacy
 * ``?token=`` query parameter, which used to leak the credential
 * into access logs, ``Referer`` outbound headers and the browser
 * history.
 *
 * Usage:
 *
 *     import { dealerApi } from "../utils/dealerApi";
 *     const r = await dealerApi.get("/dealer/stats", { token });
 *     await dealerApi.put("/dealer/targets", { token }, { monthly_target: 10 });
 *
 * The last argument for put/post/delete is the request body. Extra
 * axios config (query params other than ``token``, custom headers)
 * can be passed via the ``config`` argument:
 *
 *     dealerApi.get("/dealer/customers", { token, config: { params: { scope: "all" } } })
 *
 * The backend still accepts ``?token=`` as a fallback during the
 * migration window; this client never sends it that way, so the
 * server-side deprecation counter (``dealer_auth_query_token_used``)
 * stays at zero for our own frontend traffic.
 */
import axios from "axios";
import { API } from "../config";

const _headers = (token, extra = {}) => ({
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  ...extra,
});

const _url = (path) => `${API}${path.startsWith("/") ? path : `/${path}`}`;

export const dealerApi = {
  get: (path, { token, config = {} } = {}) =>
    axios.get(_url(path), {
      ...config,
      headers: _headers(token, config.headers),
    }),
  post: (path, { token, config = {} } = {}, data) =>
    axios.post(_url(path), data, {
      ...config,
      headers: _headers(token, config.headers),
    }),
  put: (path, { token, config = {} } = {}, data) =>
    axios.put(_url(path), data, {
      ...config,
      headers: _headers(token, config.headers),
    }),
  patch: (path, { token, config = {} } = {}, data) =>
    axios.patch(_url(path), data, {
      ...config,
      headers: _headers(token, config.headers),
    }),
  delete: (path, { token, config = {} } = {}) =>
    axios.delete(_url(path), {
      ...config,
      headers: _headers(token, config.headers),
    }),
};

export default dealerApi;
